import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { BookLogo, BookSocialLinks, PublicBookShell } from '@/components/booking/PublicBookShell'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { fetchBookingConfig } from '@/lib/api/publicBooking'

function formatWhatsAppUrl(value) {
  if (!value) {
    return null
  }

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value
  }

  const digits = value.replace(/\D+/g, '')
  return digits ? `https://wa.me/${digits}` : null
}

function ContactRow({ label, children }) {
  if (!children) {
    return null
  }

  return (
    <div className="px-1 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-[#8B7E6E]">{label}</p>
      <div className="mt-1 text-sm font-medium text-[#1F1611]">{children}</div>
    </div>
  )
}

export function BookBranchHomePage() {
  const { slug } = useParams()
  const { t } = useUiLocale()
  const [config, setConfig] = useState(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!slug) {
      return
    }

    fetchBookingConfig(slug)
      .then(setConfig)
      .catch(() => setError(t('publicBook.home.unavailable')))
  }, [slug, t])

  if (error) {
    return (
      <PublicBookShell>
        <div className="flex min-h-[100dvh] items-center justify-center px-6 pt-10 text-center text-sm text-red-600">
          {error}
        </div>
      </PublicBookShell>
    )
  }

  if (!config) {
    return (
      <PublicBookShell>
        <div className="flex min-h-[100dvh] items-center justify-center pt-10 text-sm text-[#8B7E6E]">
          {t('common.loading')}
        </div>
      </PublicBookShell>
    )
  }

  const { branding, branch } = config
  const titleLines = branding.hero_title_lines ?? [branch.name]
  const displayTitle = titleLines.join(' ')
  const catalogUrl = `/book/${slug}/catalog`
  const whatsappUrl = formatWhatsAppUrl(branch.whatsapp)
  const hasContact = Boolean(branch.phone || whatsappUrl || branch.address || branch.map_url)

  return (
    <PublicBookShell branding={branding}>
      <div className="flex min-h-[100dvh] flex-col pb-6 pt-10">
        <div className="relative mx-3 mt-3 min-h-[62dvh] overflow-hidden rounded-[44px] shadow-[0_24px_60px_-20px_rgba(58,28,12,0.45)]">
          <div className="absolute inset-0 z-0" aria-hidden="true">
            <img
              src={branding.hero_image_url}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-black/35" />
          </div>

          <div className="relative z-10 flex min-h-[62dvh] flex-col">
            <div className="flex shrink-0 justify-end p-5">
              <button
                type="button"
                aria-label={t('publicBook.aria.openMenu')}
                onClick={() => setMenuOpen(true)}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white/95 shadow-lg shadow-black/25 backdrop-blur-md"
                style={{ color: 'var(--book-primary)' }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            <div className="flex flex-1 items-center justify-center px-6 pb-6 pt-2">
              <div
                className="flex w-full max-w-sm flex-col items-center justify-between gap-8 overflow-hidden rounded-[40px] px-5 py-9 text-center"
                style={{ backgroundColor: 'rgba(31, 22, 17, 0.5)' }}
              >
                <div className="mx-auto flex h-28 w-full shrink-0 items-center justify-center overflow-hidden">
                  <BookLogo
                    url={branding.logo_url}
                    name={displayTitle || branch.name}
                    alt={t('publicBook.home.logoAlt', { name: displayTitle || branch.name })}
                  />
                </div>

                <div className="flex flex-col items-center px-2">
                  <p className="text-xs font-normal uppercase tracking-[0.42em] text-white/90">
                    {t('publicBook.home.welcome')}
                  </p>
                  <h1 className="mt-4 text-[2.5rem] font-extrabold uppercase leading-[0.92] tracking-[0.01em] text-white">
                    {displayTitle}
                  </h1>
                  <div
                    className="mt-5 inline-flex items-center gap-2.5 rounded-full px-5 py-2.5"
                    style={{ backgroundColor: 'var(--book-secondary)' }}
                  >
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-white">
                      {branding.hero_subline}
                    </p>
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
                  </div>
                </div>

                <BookSocialLinks links={branding.social_links} />
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-20 mx-3 mt-5 px-1">
          <Link
            to={catalogUrl}
            className="flex h-[68px] w-full touch-manipulation items-center justify-center rounded-full border border-white/35 text-lg font-bold uppercase tracking-[0.14em] text-white shadow-[0_22px_38px_-12px_rgba(229,115,74,0.72)] transition-transform active:scale-[0.98]"
            style={{ backgroundColor: 'var(--book-secondary)' }}
          >
            {t('publicBook.home.viewCatalog')}
          </Link>
        </div>
      </div>

      {menuOpen ? (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label={t('publicBook.aria.closeMenuBackdrop')}
            className="absolute inset-0 bg-[#1F1611]/60 backdrop-blur-[2px]"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute left-1/2 top-20 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-[30px] border border-white/60 bg-[#FBF6EC] p-5 shadow-[0_30px_80px_-20px_rgba(31,22,17,0.6)]">
            <div className="flex justify-end">
              <button
                type="button"
                aria-label={t('publicBook.aria.closeMenu')}
                onClick={() => setMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F1EAD9] text-[#1F1611] transition-all hover:bg-[#E8DFC6] active:scale-95"
              >
                ×
              </button>
            </div>
            <ul className="mt-2 divide-y divide-[#ECE3D0]">
              <li>
                <Link
                  to={catalogUrl}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between rounded-2xl px-1 py-4 transition-colors hover:bg-white/70"
                >
                  <span className="text-base font-semibold text-[#1F1611]">{t('publicBook.home.viewCatalog')}</span>
                  <span className="text-[#1F1611] book-rtl-flip">›</span>
                </Link>
              </li>
              {hasContact ? (
                <li>
                  <div className="rounded-2xl px-1 py-2">
                    <p className="px-1 py-2 text-base font-semibold text-[#1F1611]">{t('publicBook.home.contact')}</p>
                    <ContactRow label={t('publicBook.home.phone')}>
                      {branch.phone ? (
                        <a href={`tel:${branch.phone}`} className="text-[#1F1611] underline-offset-2 hover:underline">
                          {branch.phone}
                        </a>
                      ) : null}
                    </ContactRow>
                    <ContactRow label={t('publicBook.home.whatsapp')}>
                      {whatsappUrl ? (
                        <a
                          href={whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1F1611] underline-offset-2 hover:underline"
                        >
                          {branch.whatsapp}
                        </a>
                      ) : null}
                    </ContactRow>
                    <ContactRow label={t('publicBook.home.address')}>
                      {branch.address ? <p className="whitespace-pre-line leading-relaxed">{branch.address}</p> : null}
                    </ContactRow>
                    <ContactRow label={t('publicBook.home.directions')}>
                      {branch.map_url ? (
                        <a
                          href={branch.map_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#1F1611] underline-offset-2 hover:underline"
                        >
                          {t('publicBook.home.openMaps')}
                        </a>
                      ) : null}
                    </ContactRow>
                  </div>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      ) : null}
    </PublicBookShell>
  )
}
